import { useState, useEffect } from 'react';
import { X, Plus, Minus, Save, GripVertical } from 'lucide-react';
import { Button } from '@/components/ui';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

type FunctionalNodeType = '项目立项' | '数据发布' | '首次造车' | '可销售车' | 'N/A';

interface MilestoneItem {
  id: string;
  sequence: number;
  name: string;
  description: string;
  required: boolean;
  functionalNode: FunctionalNodeType;
}

interface VehicleMilestoneTemplate {
  id: string;
  oem: string;
  description: string;
  milestones: MilestoneItem[];
  created_at: string;
  updated_at: string;
  isDefault?: boolean;
}

interface VehicleMilestoneTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  editingTemplate: VehicleMilestoneTemplate | null;
}

const defaultMilestones: MilestoneItem[] = [
  { id: '1', sequence: 1, name: 'VPI', description: 'Vehicle Program Initiation', required: true, functionalNode: '项目立项' },
  { id: '2', sequence: 2, name: 'SF', description: 'Styling Freeze', required: false, functionalNode: 'N/A' },
  { id: '3', sequence: 3, name: 'VDR', description: 'Verified Data Release', required: true, functionalNode: '数据发布' },
  { id: '4', sequence: 4, name: 'MC1', description: 'Matching 1', required: false, functionalNode: 'N/A' },
  { id: '5', sequence: 5, name: 'APPV', description: 'Advanced Process Validation Vehicle', required: true, functionalNode: '首次造车' },
  { id: '6', sequence: 6, name: 'MC2', description: 'Matching 2', required: false, functionalNode: 'N/A' },
  { id: '7', sequence: 7, name: 'PPV', description: 'Process Validation Vehicle', required: false, functionalNode: 'N/A' },
  { id: '8', sequence: 8, name: 'VTC', description: 'Vehicle Testing Complete', required: false, functionalNode: 'N/A' },
  { id: '9', sequence: 9, name: 'VDC', description: 'Validation Documentation Complete', required: false, functionalNode: 'N/A' },
  { id: '10', sequence: 10, name: 'S', description: 'Salable', required: true, functionalNode: '可销售车' },
  { id: '11', sequence: 11, name: 'SORP', description: 'Start Of Regular Production', required: true, functionalNode: 'N/A' },
];

const functionalNodeOptions: FunctionalNodeType[] = ['项目立项', '数据发布', '首次造车', '可销售车', 'N/A'];
const requiredFunctionalNodes: FunctionalNodeType[] = ['首次造车', '可销售车'];

interface SortableRowProps {
  milestone: MilestoneItem;
  index: number;
  isViewMode: boolean;
  milestonesLength: number;
  onMilestoneChange: (index: number, field: keyof MilestoneItem, value: string | boolean) => void;
  onAddMilestone: (index: number) => void;
  onRemoveMilestone: (index: number) => void;
}

function SortableRow({
  milestone,
  index,
  isViewMode,
  milestonesLength,
  onMilestoneChange,
  onAddMilestone,
  onRemoveMilestone,
}: SortableRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: milestone.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    backgroundColor: isDragging ? '#f0f9ff' : undefined,
  };

  return (
    <tr ref={setNodeRef} style={style} className="hover:bg-gray-50">
      <td className="px-3 py-3">
        <div className="flex items-center gap-2">
          {!isViewMode && (
            <button
              {...attributes}
              {...listeners}
              className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
              title="拖拽排序"
            >
              <GripVertical className="w-4 h-4" />
            </button>
          )}
          <span className="inline-flex items-center justify-center w-7 h-7 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
            {milestone.sequence}
          </span>
        </div>
      </td>
      <td className="px-3 py-2">
        <input
          type="text"
          value={milestone.name}
          onChange={(e) => onMilestoneChange(index, 'name', e.target.value)}
          placeholder="节点名称"
          disabled={isViewMode}
          className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed text-sm"
        />
      </td>
      <td className="px-3 py-2">
        <input
          type="text"
          value={milestone.description}
          onChange={(e) => onMilestoneChange(index, 'description', e.target.value)}
          placeholder="描述"
          disabled={isViewMode}
          className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed text-sm"
        />
      </td>
      <td className="px-3 py-3 text-center">
        <label className="inline-flex items-center">
          <input
            type="checkbox"
            checked={milestone.required}
            onChange={(e) => onMilestoneChange(index, 'required', e.target.checked)}
            disabled={isViewMode}
            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:cursor-not-allowed"
          />
        </label>
      </td>
      <td className="px-3 py-2 text-center">
        <select
          value={milestone.functionalNode}
          onChange={(e) => onMilestoneChange(index, 'functionalNode', e.target.value as FunctionalNodeType)}
          disabled={isViewMode}
          className={`px-2 py-1 border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed text-sm ${
            milestone.functionalNode !== 'N/A' 
              ? 'border-blue-400 bg-blue-50 font-medium' 
              : 'border-gray-300'
          }`}
        >
          {functionalNodeOptions.map(option => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      </td>
      {!isViewMode && (
        <td className="px-3 py-3">
          <div className="flex items-center justify-center space-x-1">
            <button
              onClick={() => onAddMilestone(index)}
              className="p-1 text-green-600 hover:bg-green-50 rounded transition-colors"
              title="在此行后插入"
            >
              <Plus className="w-4 h-4" />
            </button>
            <button
              onClick={() => onRemoveMilestone(index)}
              disabled={milestonesLength <= 1}
              className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="删除此行"
            >
              <Minus className="w-4 h-4" />
            </button>
          </div>
        </td>
      )}
    </tr>
  );
}

export function VehicleMilestoneTemplateModal({
  isOpen,
  onClose,
  onSave,
  editingTemplate
}: VehicleMilestoneTemplateModalProps) {
  const [oem, setOem] = useState('');
  const [description, setDescription] = useState('');
  const [milestones, setMilestones] = useState<MilestoneItem[]>([]);
  const [saving, setSaving] = useState(false);

  const isDefaultTemplate = editingTemplate?.isDefault === true;
  const isViewMode = isDefaultTemplate;

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (isOpen) {
      if (editingTemplate) {
        setOem(editingTemplate.oem);
        setDescription(editingTemplate.description);
        setMilestones(editingTemplate.milestones);
      } else {
        setOem('');
        setDescription('');
        setMilestones(defaultMilestones.map(m => ({ ...m, id: Date.now().toString() + m.sequence })));
      }
    }
  }, [isOpen, editingTemplate]);

  const generateId = () => {
    return Date.now().toString() + Math.random().toString(36).substr(2, 9);
  };

  const updateSequence = (items: MilestoneItem[]) => {
    return items.map((item, index) => ({
      ...item,
      sequence: index + 1
    }));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setMilestones((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        const newItems = arrayMove(items, oldIndex, newIndex);
        return updateSequence(newItems);
      });
    }
  };

  const handleAddMilestone = (index: number) => {
    const newMilestone: MilestoneItem = {
      id: generateId(),
      sequence: 0,
      name: '',
      description: '',
      required: false,
      functionalNode: 'N/A'
    };
    
    const newMilestones = [...milestones];
    newMilestones.splice(index + 1, 0, newMilestone);
    setMilestones(updateSequence(newMilestones));
  };

  const handleRemoveMilestone = (index: number) => {
    if (milestones.length <= 1) return;
    const newMilestones = milestones.filter((_, i) => i !== index);
    setMilestones(updateSequence(newMilestones));
  };

  const handleMilestoneChange = (index: number, field: keyof MilestoneItem, value: string | boolean) => {
    const newMilestones = [...milestones];
    newMilestones[index] = {
      ...newMilestones[index],
      [field]: value
    };
    setMilestones(newMilestones);
  };

  const validateFunctionalNodes = (): string | null => {
    const nonNAFunctionalNodes = functionalNodeOptions.filter(n => n !== 'N/A');
    
    const functionalNodeCounts: Record<string, number> = {};
    nonNAFunctionalNodes.forEach(node => {
      functionalNodeCounts[node] = 0;
    });

    milestones.forEach(m => {
      if (m.functionalNode && m.functionalNode !== 'N/A') {
        functionalNodeCounts[m.functionalNode]++;
      }
    });

    const errors: string[] = [];

    for (const node of nonNAFunctionalNodes) {
      if (functionalNodeCounts[node] === 0) {
        errors.push(`功能节点"${node}"未配置`);
      }
    }

    for (const node of nonNAFunctionalNodes) {
      if (functionalNodeCounts[node] > 1) {
        const duplicateNodes = milestones
          .filter(m => m.functionalNode === node)
          .map(m => m.name);
        errors.push(`功能节点"${node}"映射了多个整车节点（${duplicateNodes.join('、')}），每个功能节点只能映射一个整车节点`);
      }
    }

    if (errors.length > 0) {
      return '无法保存：\n' + errors.join('\n');
    }

    return null;
  };

  const handleSave = async () => {
    if (!oem.trim()) {
      alert('请输入OEM名称');
      return;
    }

    if (milestones.some(m => !m.name.trim())) {
      alert('请填写所有节点名称');
      return;
    }

    const validationError = validateFunctionalNodes();
    if (validationError) {
      alert(validationError);
      return;
    }

    setSaving(true);
    try {
      const storedSession = localStorage.getItem('session');
      let token: string | null = null;
      
      if (storedSession) {
        try {
          const session = JSON.parse(storedSession);
          token = session?.access_token || null;
        } catch {
          token = null;
        }
      }

      if (!token) {
        alert('未登录，请先登录');
        setSaving(false);
        return;
      }

      const url = '/api/templates';
      const method = editingTemplate && !editingTemplate.isDefault ? 'PUT' : 'POST';
      
      const body = {
        type: 'vehicle',
        id: editingTemplate?.isDefault ? undefined : editingTemplate?.id,
        oem: oem.trim(),
        description: description.trim(),
        milestones: milestones
      };

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        onSave();
      } else {
        const error = await response.json();
        console.error('Save error:', error);
        alert(error.error || '保存失败');
      }
    } catch (error) {
      console.error('Failed to save template:', error);
      alert('保存失败，请重试');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center">
            <h3 className="text-lg font-bold text-gray-800">
              {isViewMode ? '查看 Vehicle Milestone Template' : (editingTemplate ? '编辑 Vehicle Milestone Template' : '创建 Vehicle Milestone Template')}
            </h3>
            {isDefaultTemplate && (
              <span className="ml-2 px-2 py-0.5 text-xs bg-blue-600 text-white rounded">默认模板</span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  OEM {isViewMode ? '' : <span className="text-red-500">*</span>}
                </label>
                <input
                  type="text"
                  value={oem}
                  onChange={(e) => setOem(e.target.value)}
                  placeholder="例如: SGM, VW, BMW"
                  disabled={isViewMode}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="模板描述"
                  disabled={isViewMode}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-3">
                <label className="block text-sm font-medium text-gray-700">
                  Vehicle Milestone List
                </label>
                <span className="text-xs text-gray-500">
                  {!isViewMode && '可拖拽行首的图标调整顺序，'}功能节点"首次造车"和"可销售车"必须各映射一个整车节点
                </span>
              </div>

              <div className="border border-gray-200 rounded-lg overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 w-14">序号</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600">节点名称</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600">Description</th>
                      <th className="px-3 py-3 text-center text-xs font-semibold text-gray-600 w-20">是否必填</th>
                      <th className="px-3 py-3 text-center text-xs font-semibold text-gray-600 w-28">功能节点</th>
                      {!isViewMode && (
                        <th className="px-3 py-3 text-center text-xs font-semibold text-gray-600 w-20">操作</th>
                      )}
                    </tr>
                  </thead>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={milestones.map(m => m.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <tbody className="divide-y divide-gray-200">
                        {milestones.map((milestone, index) => (
                          <SortableRow
                            key={milestone.id}
                            milestone={milestone}
                            index={index}
                            isViewMode={isViewMode}
                            milestonesLength={milestones.length}
                            onMilestoneChange={handleMilestoneChange}
                            onAddMilestone={handleAddMilestone}
                            onRemoveMilestone={handleRemoveMilestone}
                          />
                        ))}
                      </tbody>
                    </SortableContext>
                  </DndContext>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-3 p-4 border-t bg-gray-50">
          <Button variant="ghost" onClick={onClose}>
            {isViewMode ? '关闭' : '取消'}
          </Button>
          {!isViewMode && (
            <Button
              variant="primary"
              leftIcon={<Save className="w-4 h-4" />}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? '保存中...' : '保存'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
